import buttonsState from './buttons/buttons';
import linksState from './links/links';
import previewState from './response-preview/response-preview';
import {
  CONTENT_TYPES,
  DEFAULT_CONTENT,
  PREVIEW_CONFIG_DEFAULT,
} from '../../../constants/messages';
import { contentToForm } from '../../../helpers/content/content';
import { getSubs } from '../../../api/broadcast';

export default {
  namespaced: true,
  state: {
    ...buttonsState.state,
    ...linksState.state,

    // form data
    selectedResponseName: '',
    content: [],
    buttons: [],

    // sidesheet info
    initialRow: null,
    previewConfig: PREVIEW_CONFIG_DEFAULT,

    // Item creating and editing
    editingPosition: -1,
    title: '',
  },
  mutations: {
    ...buttonsState.mutations,
    ...linksState.mutations,

    setSelectedResponseName: (state, name) => {
      state.selectedResponseName = name;
    },

    // init content
    resetForm: (state, content) => {
      state.content = [];
      state.buttons = [];
      state.title = '';
      state.editingPosition = -1;
    },
    setContent: (state, content) => {
      state.content = content;
    },
    setInitialRow: (state, rowData) => {
      state.initialRow = rowData;
      if (state.initialRow) {
        state.selectedResponseName = rowData.name;
        const messageContents = contentToForm(rowData.content);
        state.content = messageContents.content;
        state.buttons = messageContents.buttons;
      } else {
        state.selectedResponseName = '';
        state.buttons = [];
        state.content = [];
      }
    },
    setTitle: (state, e) => {
      if (e && e.target && e.target.value) {
        state.title = e.target.value;
      } else {
        state.title = '';
      }
    },

    // CRUD content
    createItem(state, { type, position }) {
      const newContent = { ...DEFAULT_CONTENT[type] };
      if (position) {
        const before = state.content.slice(0, position);
        const after = state.content.slice(position, state.content.length);
        state.content = [...before, newContent, ...after];
      } else {
        state.content.push(newContent);
      }
    },
    finishEditingItem(state) {
      state.editingPosition = -1;
    },
    destroyItem(state, position) {
      const itemType = state.content[position].mode;
      switch (itemType) {
        default:
          state.content.splice(position, 1);
      }
    },
    updateItem(state, { position, data }) {
      const updated = [...state.content];
      updated[position] = data;
      state.content = updated;
    },
    setEditingPosition(state, index) {
      state.editingPosition = index;
    },
    startEditing(state, editingPosition) {
      state.editingPosition = editingPosition;
    },
    swapIndices: (state, { ind, ind2 }) => {
      const newContent = [...state.content];
      newContent[ind] = state.content[ind2];
      newContent[ind2] = state.content[ind];
      state.content = newContent;
    },
  },
  getters: {
    ...buttonsState.getters,
    ...linksState.getters,
    ...previewState.getters,
    buttonsPresent(state) {
      return !!state.buttons.length;
    },
    currentItem(state) {
      return state.content[state.editingPosition];
    },
    currentItemEmpty(state, getters) {
      switch (getters.editingMode) {
        case CONTENT_TYPES.text:
          return !getters.currentItem.text;
        case CONTENT_TYPES.link:
          return (
            !getters.currentItem.link &&
            !getters.currentItem.thumbnail &&
            !getters.currentItem.title
          );
        case CONTENT_TYPES.media:
          return !getters.currentItem.link && !getters.currentItem.title;
      }
    },
    editingItem(state) {
      return -1 < state.editingPosition || state.editingButtons;
    },
    editingMode(state, getters) {
      if (getters.editingItem) return getters.currentItem.mode;
      return '';
    },
    finalTitle(state, getters) {
      const untitled = 'Untitled Content';
      const title = state.title || untitled;
      return title;
    },
    flattenedNameOptions(state, getters, rootState) {
      const options = rootState.responseManagerList.responseNameOptions;
      if (options && options.length) {
        return options.map((name) => name.value).slice(1, options.length);
      }
      return [];
    },
    formChanged(state) {
      if (state.initialRow) {
        const initialRow = state.initialRow;
        const initialContent = contentToForm(initialRow.content);
        const nameChanged = state.selectedResponseName !== initialRow.name;
        const messageChanged =
          JSON.stringify(initialContent.content) !==
          JSON.stringify(state.content);
        const buttonsChanged =
          JSON.stringify(initialContent.buttons) !==
          JSON.stringify(state.buttons);
        return nameChanged || messageChanged || buttonsChanged;
      } else {
        return false;
      }
    },
    messageReady(state, getters) {
      if (state.content.length && !getters.editingItem) return true;
      return false;
    },
    selectedResponseNameValid(state, getters) {
      return getters.flattenedNameOptions.includes(state.selectedResponseName);
    },
    sendable(state, getters) {
      const exportMessageBody = (data, item, index, content) => {
        let dataUpdate;
        switch (item.mode) {
          case 'text':
            dataUpdate = [...data, { text: item.text }];
            break;
          case 'link':
            const alreadyAdded =
              0 < index && CONTENT_TYPES.link === content[index - 1].mode;
            if (alreadyAdded) {
              return data;
            } else {
              dataUpdate = [
                ...data,
                {
                  attachments: getters.links.map((link) => {
                    return {
                      title: link.title,
                      image: link.thumbnail,
                      link: link.link,
                      readMoreButtonText: link.readMoreButtonText,
                    };
                  }),
                },
              ];
            }
            break;
          case 'media':
            const mediaData = {
              link: item.link,
              type: item.type,
            };
            if (item.title) mediaData.title = item.title;
            if (item.thumbnail) mediaData.thumbnail = item.thumbnail;
            dataUpdate = [...data, { media: [mediaData] }];
            break;
        }
        return dataUpdate;
      };

      const postBodyContent = state.content.reduce(
        (data, item, index, content) =>
          exportMessageBody(data, item, index, content),
        []
      );
      if (state.buttons.length && state.content.length) {
        postBodyContent[postBodyContent.length - 1].choices = state.buttons.map(
          (item) => {
            return {
              title: item.title,
              returnValue: item.returnValue || item.title,
            };
          }
        );
      }
      return postBodyContent;
    },
  },
  actions: {
    ...buttonsState.actions,
    ...linksState.actions,

    // audience
    getAudienceOptions: async ({ commit, getters, state, rootState }) => {
      try {
        const audienceOptions = {};
        const subs = await getSubs(rootState.user.botData.key);
        audienceOptions[rootState.user.botData.key] = subs;
        commit('setAudienceOptions', audienceOptions);
      } catch (e) {
        // eslint-disable-next-line
        console.log('error getting audience options', e);
      }
    },

    // content
    addItem({ commit, state, getters }, type) {
      if (getters.editingItem) return;

      if (CONTENT_TYPES.link === type && getters.linksPresent) {
        const insertPosition =
          0 < getters.lastLinkIndex ? getters.lastLinkIndex + 1 : 0;
        commit('createItem', { type, position: insertPosition });
        commit('setEditingPosition', getters.lastLinkIndex);
      } else {
        commit('createItem', { type });
        commit('setEditingPosition', state.content.length - 1);
      }
    },
    cancelItem({ commit, dispatch, getters, state }) {
      if (getters.currentItemEmpty) {
        dispatch('deleteItem', state.editingPosition);
      }
      commit('finishEditingItem');
    },
    saveItem({ commit }, { data, position }) {
      commit('updateItem', { data, position });
      commit('finishEditingItem');
    },
    deleteItem({ commit }, position) {
      commit('destroyItem', position);
      commit('finishEditingItem');
    },
    swapItems({ commit, dispatch, state }, { ind, ind2 }) {
      commit('swapIndices', { ind, ind2 });
    },
    handleCloseSideSheet({ commit }) {
      commit('responseManager/setSidesheetOpen', false, { root: true });
      commit('setInitialRow', null);
    },
    clear: ({ commit }) => {
      commit('setAudience', '');
      commit('resetForm');
    },
  },
};
